import { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Plus, RefreshCw, Pencil, Trash2, Loader2, FileText, Play, Copy, Building2, X,
} from 'lucide-react';
import { api } from '../lib/auth';
import { toast } from 'sonner';

const EMPTY = {
  label: '',
  account_id: '',
  from_pattern: '',
  subject_pattern: '',
  body_pattern: '',
  has_attachments: true,
  tag_firm_id: '',
  tag_year: '',
  tag_label: '',
  archive_type: 'classic',
  extract_zip_attachments: false,
  active: true,
  // Dynamic routing: operators can list [{keyword, firm_id}] so ONE rule
  // dispatches mails to the matching firm based on subject content.
  // Stored as array in the UI for easy add/remove; serialised to the
  // backend as `{keyword: firm_id}` map when saving.
  firm_keyword_rows: [],
};

export default function MailArchiveRules() {
  const [rules, setRules] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [rRes, aRes, fRes] = await Promise.all([
        api.get('/mail/archive-rules'),
        api.get('/mail/accounts'),
        api.get('/firms/').catch(() => ({ data: { data: [] } })),
      ]);
      setRules(rRes.data?.data || []);
      setAccounts(aRes.data?.data || []);
      setFirms(fRes.data?.data || []);
    } catch {
      toast.error('Yüklenemedi');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const onAdd = () => { setEditId(null); setForm(EMPTY); setEditOpen(true); };
  const onEdit = (r) => {
    setEditId(r.id);
    setForm({ ...EMPTY, ...r,
      account_id: r.account_id || '',
      tag_firm_id: r.tag_firm_id || '',
      tag_year: r.tag_year || '',
      from_pattern: r.from_pattern || '',
      subject_pattern: r.subject_pattern || '',
      body_pattern: r.body_pattern || '',
      tag_label: r.tag_label || '',
      archive_type: r.archive_type || 'classic',
      extract_zip_attachments: !!r.extract_zip_attachments,
      firm_keyword_rows: Object.entries(r.firm_keyword_map || {}).map(([k, v]) => ({ keyword: k, firm_id: v })),
    });
    setEditOpen(true);
  };

  const onDuplicate = (r) => {
    setEditId(null);
    setForm({
      ...EMPTY, ...r,
      label: `${r.label} (kopya)`,
      tag_firm_id: '',
      account_id: r.account_id || '',
      tag_year: r.tag_year || '',
      from_pattern: r.from_pattern || '',
      subject_pattern: r.subject_pattern || '',
      body_pattern: r.body_pattern || '',
      tag_label: r.tag_label || '',
      archive_type: r.archive_type || 'classic',
      extract_zip_attachments: !!r.extract_zip_attachments,
      firm_keyword_rows: Object.entries(r.firm_keyword_map || {}).map(([k, v]) => ({ keyword: k, firm_id: v })),
    });
    setEditOpen(true);
    toast.info('Kuralın bir kopyası açıldı — firmayı seçip kaydet.');
  };

  const submit = async () => {
    setBusy(true);
    const fmap = {};
    for (const row of (form.firm_keyword_rows || [])) {
      if (row.keyword && row.firm_id) fmap[row.keyword.trim()] = row.firm_id;
    }
    const payload = {
      ...form,
      account_id: form.account_id || null,
      tag_firm_id: form.tag_firm_id || null,
      tag_year: form.tag_year ? parseInt(form.tag_year, 10) : null,
      from_pattern: form.from_pattern || null,
      subject_pattern: form.subject_pattern || null,
      body_pattern: form.body_pattern || null,
      tag_label: form.tag_label || null,
      archive_type: form.archive_type === 'classic' ? null : form.archive_type,
      extract_zip_attachments: !!form.extract_zip_attachments,
      firm_keyword_map: Object.keys(fmap).length > 0 ? fmap : null,
    };
    delete payload.firm_keyword_rows;
    try {
      if (editId) await api.put(`/mail/archive-rules/${editId}`, payload);
      else await api.post('/mail/archive-rules', payload);
      toast.success('Kaydedildi');
      setEditOpen(false);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`"${r.label}" kuralını silmek istediğine emin misin?`)) return;
    try {
      await api.delete(`/mail/archive-rules/${r.id}`);
      toast.success('Silindi');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    }
  };

  const runAll = async () => {
    try {
      const t = toast.loading('Kurallar çalıştırılıyor...');
      const res = await api.post('/mail/archive-rules/run');
      toast.dismiss(t);
      const d = res.data?.data || {};
      toast.success(`${d.archived || 0} ek arşivlendi (${d.scanned} mesaj, ${d.rules} kural)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Çalıştırılamadı');
    }
  };

  const runOne = async (r) => {
    try {
      const t = toast.loading(`"${r.label}" çalıştırılıyor...`);
      const res = await api.post(`/mail/archive-rules/${r.id}/run`);
      toast.dismiss(t);
      if (res.data?.success === false) {
        toast.error(res.data?.detail || 'Çalıştırılamadı');
        return;
      }
      const d = res.data?.data || {};
      toast.success(`"${r.label}" → ${d.archived || 0} ek arşivlendi (${d.scanned} mesaj tarandı)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Çalıştırılamadı');
    }
  };

  return (
    <div className="p-4 max-w-4xl mx-auto space-y-4" data-testid="mail-rules-page">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <FileText className="h-5 w-5 text-emerald-500" /> Mail Arşivleme Kuralları
        </h1>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={runAll} data-testid="rules-run-all">
            <Play className="h-4 w-4 mr-1" /> Tümünü Çalıştır
          </Button>
          <Button size="sm" onClick={onAdd} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="rule-add">
            <Plus className="h-4 w-4 mr-1" /> Yeni Kural
          </Button>
          <Button size="sm" variant="outline" onClick={refresh} disabled={loading} data-testid="rules-refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <p className="text-xs text-slate-500">
        Bir kural eşleşince mesajdaki tüm ekler <b>arşive</b> kopyalanır. Tarihten otomatik <b>yıl etiketi</b> atanır.
        Eşleşme: <b>Gönderen + Konu + Gövde</b> regex desenlerinden hangisi doluysa (boş alanlar atlanır).
      </p>

      <div className="grid gap-3">
        {rules.length === 0 && !loading && (
          <Card className="p-8 text-center text-slate-400 text-sm">Henüz kural yok.</Card>
        )}
        {rules.map((r) => (
          <Card key={r.id} className="p-3" data-testid={`rule-row-${r.id}`}>
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{r.label}</span>
                  {r.active ? (
                    <Badge className="bg-emerald-500 text-[10px]">aktif</Badge>
                  ) : (
                    <Badge variant="outline" className={`text-[10px] ${r.deactivated_reason === 'account_deactivated' ? 'text-amber-700 border-amber-200' : ''}`}>
                      pasif{r.deactivated_reason === 'account_deactivated' ? ' · hesap pasif' : ''}
                    </Badge>
                  )}
                  {r.archive_type === 'postnl_distribution' && (
                    <Badge className="bg-blue-500 text-[10px]">PostNL · ZIP→PDF</Badge>
                  )}
                  {r.tag_firm_id ? (
                    <Badge variant="outline" className="text-[10px] flex items-center gap-1 text-blue-700 border-blue-200">
                      <Building2 className="h-3 w-3" />
                      {firms.find((f) => f.id === r.tag_firm_id)?.name || r.tag_firm_id.slice(0, 8)}
                    </Badge>
                  ) : r.firm_keyword_map && Object.keys(r.firm_keyword_map).length > 0 ? (
                    <Badge variant="outline" className="text-[10px] flex items-center gap-1 text-violet-700 border-violet-200" title={Object.keys(r.firm_keyword_map).join(', ')}>
                      <Building2 className="h-3 w-3" />
                      Dinamik → {Object.keys(r.firm_keyword_map).length} firma
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-200">⚠ firma yok</Badge>
                  )}
                  {r.tag_label && <Badge variant="outline" className="text-[10px]">#{r.tag_label}</Badge>}
                  {r.tag_year && <Badge variant="outline" className="text-[10px]">{r.tag_year}</Badge>}
                </div>
                <div className="text-[11px] text-slate-500 mt-1 space-y-0.5">
                  {r.account_id && <div>Hesap: <code>{accounts.find((a) => a.id === r.account_id)?.label || r.account_id}</code></div>}
                  {r.from_pattern && <div>Gönderen: <code>{r.from_pattern}</code></div>}
                  {r.subject_pattern && <div>Konu: <code>{r.subject_pattern}</code></div>}
                  {r.body_pattern && <div>Gövde: <code>{r.body_pattern}</code></div>}
                  {r.has_attachments && <div className="italic">Sadece eki olan mesajlarda</div>}
                </div>
              </div>
              <Button size="sm" variant="outline" className="h-8 text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => runOne(r)} title="Sadece bu kuralı tüm mesajlara uygula" data-testid={`rule-runone-${r.id}`}>
                <Play className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="outline" className="h-8" onClick={() => onDuplicate(r)} title="Bu kuralın bir kopyasını oluştur (örn. başka bir firma için)" data-testid={`rule-copy-${r.id}`}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="outline" className="h-8" onClick={() => onEdit(r)} data-testid={`rule-edit-${r.id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={() => remove(r)} data-testid={`rule-del-${r.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 overflow-hidden" data-testid="rule-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle>{editId ? 'Kuralı düzenle' : 'Yeni arşiv kuralı'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2.5 px-6 py-4 overflow-y-auto flex-1">
            <Field label="Etiket *">
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Aytu Faturaları" data-testid="rule-form-label" />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Hesap (boş = tüm hesaplar)">
                <Select value={form.account_id || '__any__'} onValueChange={(v) => setForm({ ...form, account_id: v === '__any__' ? '' : v })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any__">Tümü</SelectItem>
                    {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Etiket / Klasör adı">
                <Input value={form.tag_label} onChange={(e) => setForm({ ...form, tag_label: e.target.value })} placeholder="örn. faturalar" data-testid="rule-form-tag-label" />
              </Field>
            </div>
            <Field label="Gönderen (regex)">
              <Input value={form.from_pattern} onChange={(e) => setForm({ ...form, from_pattern: e.target.value })} placeholder="@aytu\\.com|info@firma" />
            </Field>
            <Field label="Konu (regex)">
              <Input value={form.subject_pattern} onChange={(e) => setForm({ ...form, subject_pattern: e.target.value })} placeholder="fatura|invoice" />
            </Field>
            <Field label="Gövde (regex)">
              <Input value={form.body_pattern} onChange={(e) => setForm({ ...form, body_pattern: e.target.value })} placeholder="(boş bırakabilirsin)" />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Firma * (PDF'ler bu firmanın arşivine düşer)">
                <Select value={form.tag_firm_id || '__none__'} onValueChange={(v) => setForm({ ...form, tag_firm_id: v === '__none__' ? '' : v })}>
                  <SelectTrigger className={`h-8 text-xs ${!form.tag_firm_id ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/10' : ''}`} data-testid="rule-form-firm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Yok (sadece süper admin görür) —</SelectItem>
                    {firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Yıl etiketi (boş = mesajın yılı)">
                <Input type="number" value={form.tag_year} onChange={(e) => setForm({ ...form, tag_year: e.target.value })} placeholder="2026" />
              </Field>
            </div>
            <p className="text-[10px] text-slate-500 -mt-1">
              Firma seçilirse: süper admin <b>tümünü</b>, o firmanın admini <b>sadece kendi</b> arşivini görür.
            </p>

            {/* ── Dynamic firm routing (keyword → firm) ───────────── */}
            <div className="rounded-md border border-blue-200 dark:border-blue-900 p-2.5 bg-blue-50/40 dark:bg-blue-950/20">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-blue-900 dark:text-blue-200">
                  Firma Anahtar Kelime Haritası <span className="font-normal text-slate-500">(isteğe bağlı · tek kural, N firma)</span>
                </Label>
                <Button
                  size="sm" variant="outline" className="h-6 text-[10px]"
                  onClick={() => setForm({ ...form, firm_keyword_rows: [...(form.firm_keyword_rows || []), { keyword: '', firm_id: '' }] })}
                  data-testid="rule-keyword-add"
                >
                  <Plus className="h-3 w-3 mr-0.5" /> Kelime ekle
                </Button>
              </div>
              <p className="text-[10px] text-slate-500 mt-1 mb-2">
                Subject'te <b>HYK</b> varsa HYK arşivine, <b>AYTU</b> varsa AYTU arşivine düşer. Eşleşme bulunamazsa yukarıdaki <b>Firma</b> alanına düşer.
              </p>
              {(form.firm_keyword_rows || []).length === 0 && (
                <p className="text-[10px] italic text-slate-400">Henüz anahtar yok — tek firma için normal Firma alanı yeterli.</p>
              )}
              <div className="space-y-1">
                {(form.firm_keyword_rows || []).map((row, idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`rule-keyword-row-${idx}`}>
                    <Input
                      className="h-7 text-xs flex-1"
                      placeholder="örn. HYK"
                      value={row.keyword}
                      onChange={(e) => {
                        const next = [...form.firm_keyword_rows];
                        next[idx] = { ...next[idx], keyword: e.target.value };
                        setForm({ ...form, firm_keyword_rows: next });
                      }}
                      data-testid={`rule-keyword-input-${idx}`}
                    />
                    <span className="text-slate-400 text-xs">→</span>
                    <Select
                      value={row.firm_id || '__none__'}
                      onValueChange={(v) => {
                        const next = [...form.firm_keyword_rows];
                        next[idx] = { ...next[idx], firm_id: v === '__none__' ? '' : v };
                        setForm({ ...form, firm_keyword_rows: next });
                      }}
                    >
                      <SelectTrigger className="h-7 text-xs flex-1" data-testid={`rule-keyword-firm-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Firma seç —</SelectItem>
                        {firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
                      onClick={() => setForm({ ...form, firm_keyword_rows: form.firm_keyword_rows.filter((_, i) => i !== idx) })}
                      data-testid={`rule-keyword-del-${idx}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2.5 bg-slate-50 dark:bg-slate-800/40">
              <Field label="Arşiv Tipi">
                <Select value={form.archive_type} onValueChange={(v) => setForm({ ...form, archive_type: v, extract_zip_attachments: v === 'postnl_distribution' ? true : form.extract_zip_attachments })}>
                  <SelectTrigger className="h-8 text-xs" data-testid="rule-form-archive-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="classic">Klasik (sadece ek dosyaları arşivle)</SelectItem>
                    <SelectItem value="postnl_distribution">PostNL Distributielijsten (ZIP'i aç + PDF'leri firmaya kaydet)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {form.archive_type === 'postnl_distribution' && (
                <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-600 dark:text-slate-300">
                  <Switch checked={form.extract_zip_attachments} onCheckedChange={(v) => setForm({ ...form, extract_zip_attachments: v })} id="extZ" data-testid="rule-form-extract-zip" />
                  <Label htmlFor="extZ" className="text-[11px]">ZIP eklerini aç ve PDF'leri PostNL arşivine ekle</Label>
                </div>
              )}
              {form.archive_type === 'postnl_distribution' && !form.tag_firm_id && (
                <p className="mt-2 text-[10px] text-amber-600">⚠ PostNL kuralında <b>Firma</b> seçmek zorunlu — PDF'ler hangi firmanın arşivine eklenecek?</p>
              )}
            </div>
            <div className="flex items-center gap-3 pt-1">
              <Switch checked={form.has_attachments} onCheckedChange={(v) => setForm({ ...form, has_attachments: v })} id="hasA" />
              <Label htmlFor="hasA" className="text-xs">Sadece eki olan mesajlar</Label>
              <span className="ml-auto" />
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} id="rActive" />
              <Label htmlFor="rActive" className="text-xs">Aktif</Label>
            </div>
          </div>
          <DialogFooter className="px-6 py-3 border-t border-slate-100 dark:border-slate-800 shrink-0 bg-white dark:bg-slate-900">
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={busy}>Vazgeç</Button>
            <Button onClick={submit} disabled={busy || !form.label} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="rule-form-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] text-slate-500">{label}</Label>
      {children}
    </div>
  );
}
